// components/Nav/Nav.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Nav.css";

export default function Nav({ categories, onNavClick }) {
  const [activeSlug, setActiveSlug] = useState(categories[0]?.slug || "");
  const navRef = useRef(null);

  /* ----------------------------------------------------
      Center active button
  ---------------------------------------------------- */
  const centerButton = useCallback((slug) => {
    const nav = navRef.current;
    const btn = document.getElementById(`btn-${slug}`);

    if (!nav || !btn) {
      setTimeout(() => centerButton(slug), 50);
      return;
    }

    const navRect = nav.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const offset =
      btnRect.left - navRect.left - navRect.width / 2 + btnRect.width / 2;

    nav.scrollBy({ left: offset, behavior: "smooth" });
  }, []);

  /* ----------------------------------------------------
      Smooth scroll + retry
  ---------------------------------------------------- */
  const scrollToSlug = useCallback(
    (slug) => {
      const attempt = () => {
        const section = document.getElementById(slug);

        if (section) {
          section.scrollIntoView({ behavior: "smooth" });
          centerButton(slug);
        } else {
          setTimeout(attempt, 100);
        }
      };

      attempt();
    },
    [centerButton]
  );

  /* ----------------------------------------------------
      Click event
  ---------------------------------------------------- */
  const handleClick = (slug) => {
    setActiveSlug(slug);

    // scroll (internal logic)
    scrollToSlug(slug);

    // external callback (QrMenuPage.jsx needs this)
    if (onNavClick) onNavClick(slug);
  };

  /* ----------------------------------------------------
      Scroll spy
  ---------------------------------------------------- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const slug = entry.target.id;

          if (entry.isIntersecting) {
            setActiveSlug(slug);
            centerButton(slug);
          }
        });
      },
      { threshold: 0.25 }
    );

    categories.forEach(({ slug }) => {
      const section = document.getElementById(slug);
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, [categories, centerButton]);

  /* ----------------------------------------------------
      Mouse drag scroll
  ---------------------------------------------------- */
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const down = (e) => {
      isDown = true;
      nav.classList.add("dragging");
      startX = e.pageX - nav.offsetLeft;
      scrollLeft = nav.scrollLeft;
    };

    const leave = () => {
      isDown = false;
      nav.classList.remove("dragging");
    };

    const up = () => {
      isDown = false;
      nav.classList.remove("dragging");
    };

    const move = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - nav.offsetLeft;
      const walk = (x - startX) * 1.5;
      nav.scrollLeft = scrollLeft - walk;
    };

    nav.addEventListener("mousedown", down);
    nav.addEventListener("mouseleave", leave);
    nav.addEventListener("mouseup", up);
    nav.addEventListener("mousemove", move);

    return () => {
      nav.removeEventListener("mousedown", down);
      nav.removeEventListener("mouseleave", leave);
      nav.removeEventListener("mouseup", up);
      nav.removeEventListener("mousemove", move);
    };
  }, []);

  /* ----------------------------------------------------
      Render
  ---------------------------------------------------- */
  return (
    <nav className="qr-nav" ref={navRef}>
      <div className="qr-nav-buttons">
        {categories.map(({ name, slug }) => (
          <button
            key={slug}
            id={`btn-${slug}`}
            className={`qr-nav-button ${activeSlug === slug ? "active" : ""}`}
            onClick={() => handleClick(slug)}
          >
            {name}
          </button>
        ))}
      </div>
    </nav>
  );
}
